// @ts-strict-ignore
import {AttrsSchema, Component} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {RetentionTrackingEvents as Events} from 'common/report/retention/util/constants';
import {DisplaySize, Placement} from 'common/components/tooltip/types';
import {TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {IronContextRegistry} from 'common/report/context-registry';
import {ChangeEvent} from 'common/components/select/types';
import {Bucket} from 'common/widgets/bucket-sizes';
import {pluralize} from 'common/util/string';
import {dataGroupStore} from 'common/report/stores/data-groups';
import {getCurrentMoment} from 'common/util/date';
import {SwitchEl} from 'common/components/switch';
import {SectionItem} from 'common/components/items-menu/types';
import {ChartType, RetentionAlignmentType, RetentionUnboundedModeType} from 'common/types/reports/bookmark';
import onImg from 'common/assets/images/retention-criteria/on.webp';
import onOrAfterImg from 'common/assets/images/retention-criteria/on_or_after.webp';
import onOrBeforeImg from 'common/assets/images/retention-criteria/on_or_before.webp';
import streakImg from 'common/assets/images/retention-criteria/streak.webp';

import {RetentionQuerySettingsUpdate} from '../retention-query-settings';
import {template} from './template';
import {CUSTOM_RETENTION_UNIT} from './constants';
import css from './index.module.styl?inline';

const DEFAULT_COLLAPSIBLE_OPEN = true;
interface RetentionQuerySettingsState {
  customRetentionUnitSelected: boolean;
  isBlockOpen: boolean;
  hovered: boolean;
}

interface RetentionQueryAttrs {
  'unbounded-mode': RetentionUnboundedModeType;
  'data-group-id': string;
  'bucket-sizes': number[];
  'retention-unit': TimeUnit;
  'retention-alignment-type': RetentionAlignmentType;
  'disable-cohortize': boolean;
  'chart-type': ChartType;
}

interface RetentionCriteriaJsxProps {
  attrs?: RetentionQueryAttrs;
  on?: {
    changeSetting: (ev: RetentionQuerySettingsUpdate) => void;
    changeUnit: (ev: RetentionUnitUpdate) => void;
    applyBucketSizes: (ev: RetentionBucketSizeUpdate) => void;
    'toggle-retention-alignment': (ev: RetentionAlignmentUpdate) => void;
    'toggle-retention-cohortize': (ev: RetentionCohortizeUpdate) => void;
  };
}

const commonTooltipAttrs = {
  size: DisplaySize.Small,
};

export type RetentionUnitUpdate = CustomEvent<{
  retentionUnit: TimeUnit;
}>;

export type RetentionBucketSizeUpdate = CustomEvent<{
  retentionUnit: TimeUnit;
  bucketSizes: number[];
}>;

export type RetentionAlignmentUpdate = CustomEvent<{
  retentionAlignmentType: RetentionAlignmentType;
}>;

export type RetentionCohortizeUpdate = CustomEvent<{
  disableCohortize: boolean;
}>;

export class RetentionCriteria extends Component<
  RetentionQuerySettingsState,
  RetentionQueryAttrs,
  EmptyObj,
  EmptyObj,
  IronContextRegistry
> {
  static get attrsSchema(): AttrsSchema<RetentionQueryAttrs> {
    return {
      ...super.attrsSchema,
      'unbounded-mode': {
        type: `string`,
        default: `none`,
      },
      'data-group-id': `string`,
      'bucket-sizes': {
        type: `json`,
        default: [],
      },
      'retention-unit': {
        type: `string`,
        default: TimeUnit.Day,
        enum: [TimeUnit.Day, TimeUnit.Month, TimeUnit.Week],
      },
      'retention-alignment-type': {
        type: `string`,
        default: RetentionAlignmentType.Birth,
        enum: Object.values(RetentionAlignmentType),
      },
      'disable-cohortize': {
        type: `boolean`,
      },
      'chart-type': {
        type: `string`,
        enum: Object.values(ChartType),
      },
    };
  }

  get config() {
    return {
      contexts: [`currentProject` as const, `i18n` as const],

      defaultState: {
        customRetentionUnitSelected: false,
        isBlockOpen: DEFAULT_COLLAPSIBLE_OPEN,
        hovered: false,
      },

      template,
      useShadowDom: true,
      css,
    };
  }

  get t() {
    return this.getContext(`i18n`).t;
  }

  track(eventName: string, props: any) {
    mixpanel.track(`[mp-query-builder] ${eventName}`, {
      report: `retention`,
      ...props,
    });
  }

  getRetentionAlignmentModifier(alignmentType: RetentionAlignmentType) {
    return alignmentType === RetentionAlignmentType.Birth ? `` : `Calendar `;
  }

  isCalendarAlignment(alignmentType: RetentionAlignmentType) {
    return alignmentType !== RetentionAlignmentType.Birth;
  }

  getLabel(bucketSizes: number[], bucketUnit: TimeUnit, alignmentType: RetentionAlignmentType) {
    const map = this.isCalendarAlignment(alignmentType)
      ? {
          [TimeUnit.Day]: this.t(`query-builder.retention.interval.label.calendar-day`),
          [TimeUnit.Week]: this.t(`query-builder.retention.interval.label.calendar-week`),
          [TimeUnit.Month]: this.t(`query-builder.retention.interval.label.calendar-month`),
        }
      : {
          [TimeUnit.Day]: this.t(`query-builder.retention.interval.label.day`),
          [TimeUnit.Week]: this.t(`query-builder.retention.interval.label.week`),
          [TimeUnit.Month]: this.t(`query-builder.retention.interval.label.month`),
        };

    const label = this.getBucketLabel(bucketSizes);
    const unit = map[bucketUnit];
    return `${unit} ${label}`;
  }

  getBucketLabel(bucketSizes: number[]) {
    let previousBucket = 0;
    const label = bucketSizes
      .map((bucket) => {
        const currentBucket = previousBucket + bucket;
        const retLabel = bucket === 1 ? `${currentBucket - 1}` : `${previousBucket}-${currentBucket - 1}`;
        previousBucket = currentBucket;
        return retLabel;
      })
      .join(` / `);
    return label;
  }

  getUnitSelectorTooltip = () =>
    this.attr(`bucket-sizes`) && this.attr(`bucket-sizes`).length
      ? {
          text: `Returning users on ${this.attr(`retention-unit`)}s: ${this.getBucketLabel(this.attr(`bucket-sizes`))}`,
          ...commonTooltipAttrs,
        }
      : commonTooltipAttrs;

  getRetentionUnitTooltip = (retentionUnit: TimeUnit) => {
    const dataGroupName = pluralize(dataGroupStore.value?.[this.attr(`data-group-id`)]?.displayName || `Users`, true);
    const alignmentModifier = this.getRetentionAlignmentModifier(this.attr(`retention-alignment-type`));
    const map = {
      [TimeUnit.Day]: `Day`,
      [TimeUnit.Week]: `Week`,
      [TimeUnit.Month]: `Month`,
    };
    const UNBOUNDED_MODE_TEXT_MAPPING = {
      none: ``,
      carry_back: `or after`,
      carry_forward: `or before`,
      consecutive_forward: `consecutively`,
      undefined: ``,
      null: ``,
    };
    return `${dataGroupName} Who Return on ${
      UNBOUNDED_MODE_TEXT_MAPPING[this.attr(`unbounded-mode`)]
    } each ${alignmentModifier}${map[retentionUnit]}`;
  };

  getRetentionUnboundedOptions = () => {
    const options = [
      {
        label: ({t}) => t(`query-builder.retention.criteria.on-or-after`),
        value: `carry_back`,
        isSelected: this.attr(`unbounded-mode`) === `carry_back`,
        tooltip: {
          text: ({t}) => t(`query-builder.retention.criteria.tooltip.on-or-after`),
          'image-src': onOrAfterImg,
          ...commonTooltipAttrs,
        },
      },
      {
        label: ({t}) => t(`query-builder.retention.criteria.on`),
        isSelected: !this.attr(`unbounded-mode`) || this.attr(`unbounded-mode`) === `none`,
        value: `none`,
        tooltip: {
          text: ({t}) => t(`query-builder.retention.criteria.tooltip.on`),
          'image-src': onImg,
          ...commonTooltipAttrs,
        },
      },
      {
        label: ({t}) => t(`query-builder.retention.criteria.on-or-before`),
        value: `carry_forward`,
        isSelected: this.attr(`unbounded-mode`) === `carry_forward`,
        tooltip: {
          text: ({t}) => t(`query-builder.retention.criteria.tooltip.on-or-before`),
          'image-src': onOrBeforeImg,
          ...commonTooltipAttrs,
        },
      },
      {
        label: ({t}) => t(`query-builder.retention.criteria.consecutive-forward`),
        value: `consecutive_forward`,
        isSelected: this.attr(`unbounded-mode`) === `consecutive_forward`,
        tooltip: {
          text: ({t}) => t(`query-builder.retention.criteria.tooltip.consecutive-forward`),
          'image-src': streakImg,
          ...commonTooltipAttrs,
        },
      },
    ];

    return options;
  };

  getAdvancedMenuOptions = (): SectionItem[] => {
    const timezone = this.getContext(`currentProject`).get()?.value?.timezone;
    const {t, formatDate} = this.getContext(`i18n`);
    const today = formatDate(getCurrentMoment({timezone}), {
      month: `short`,
      day: `numeric`,
    });
    const tomorrow = formatDate(getCurrentMoment({timezone}).add(1, `days`), {
      month: `short`,
      day: `numeric`,
    });

    const items: SectionItem[] = [
      {
        label: t(`retention.group-by-date`),
        tooltip: {
          placement: Placement.Top,
          'max-width': `185px`,
          text: t(`retention.group-by-date-tooltip`, {today, tomorrow}),
        },
        actionsRenderer: () =>
          SwitchEl({
            attrs: {
              on: this.attr(`retention-alignment-type`) === RetentionAlignmentType.IntervalStart,
            },
            on: {
              change: this.handleToggleRetentionAlignment,
            },
          }),
      },
    ];

    if (this.attr(`chart-type`) !== ChartType.Line) {
      items.push({
        label: t(`retention.cohortize`),
        tooltip: {
          placement: Placement.Top,
          'max-width': `185px`,
          text: t(`retention.cohortize-tooltip`),
        },
        actionsRenderer: () =>
          SwitchEl({
            attrs: {
              on: this.attr(`disable-cohortize`) !== true,
            },
            on: {
              change: this.handleToggleCohortize,
            },
          }),
      });
    }

    return items;
  };

  handleChangeUnitSelector = (ev) => {
    if (ev.detail.item.value === CUSTOM_RETENTION_UNIT) {
      this.update({customRetentionUnitSelected: true});
    } else {
      const retentionUnit: TimeUnit = ev.detail.item.value;
      const changeUnitEvent: RetentionUnitUpdate = new CustomEvent(`changeUnit`, {
        detail: {retentionUnit},
      });
      this.dispatchEvent(changeUnitEvent);
    }
  };

  handleChangeCountingMethodSelector = (ev: CustomEvent) => {
    const changeSettingEvent: RetentionQuerySettingsUpdate = new CustomEvent(`changeSetting`, {
      detail: {
        unboundedMode: ev.detail.item.value,
      },
    });
    this.dispatchEvent(changeSettingEvent);
    this.track(Events.UnboundedMode, {UnboundedMode: ev.detail.item.value});
  };

  handleCollapsibleQuerySectionChange = (ev: CustomEvent) => {
    const isBlockOpen = ev.detail.state === `open`;
    this.update({isBlockOpen});
    this.track(Events.ClickedRetentionCriteria, {Action: isBlockOpen ? `expand` : `collapse`});
  };

  handleToggleRetentionAlignment = (ev: CustomEvent) => {
    const {
      detail: {on},
    } = ev;
    this.dispatchEvent(
      new CustomEvent(`toggle-retention-alignment`, {
        detail: {retentionAlignmentType: on ? `interval_start` : `birth`},
      }),
    );

    this.track(Events.IntervalAlignment, {AlignmentType: on ? `interval_start` : `birth`});
  };

  handleApplyCustom = (ev: CustomEvent<{bucketUnit: TimeUnit; buckets: Bucket[]}>) => {
    this.update({customRetentionUnitSelected: false});
    const changeBucketEvent: RetentionBucketSizeUpdate = new CustomEvent(`applyBucketSizes`, {
      detail: {
        retentionUnit: ev.detail.bucketUnit,
        bucketSizes: ev.detail.buckets.map((bucket) => bucket.interval),
      },
    });
    this.dispatchEvent(changeBucketEvent);
    this.track(Events.AddCustomRetentionBucket, {});
  };

  handleCancelCustom = () => {
    this.update({customRetentionUnitSelected: false});
  };

  handleAdvancedMenuChange = (ev: ChangeEvent) => {
    if ([`none`, `carry_back`, `carry_forward`, `consecutive_forward`].includes(ev.detail.item.value)) {
      const changeSettingEvent: RetentionQuerySettingsUpdate = new CustomEvent(`changeSetting`, {
        detail: {
          unboundedMode: ev.detail.item.value,
        },
      });
      this.dispatchEvent(changeSettingEvent);
    }
    if (Object.values(RetentionAlignmentType).includes(ev.detail.item.value)) {
      this.dispatchEvent(
        new CustomEvent(`toggle-retention-alignment`, {
          detail: {retentionAlignmentType: ev.detail.item.value},
        }),
      );
    }
  };

  handleMouseEnterUnitSelector = () => {
    this.update({
      hovered: true,
    });
  };

  handleMouseLeaveUnitSelector = () => {
    this.update({
      hovered: false,
    });
  };

  handleToggleCohortize = (ev: CustomEvent) => {
    const {
      detail: {on},
    } = ev;
    this.dispatchEvent(
      new CustomEvent(`toggle-retention-cohortize`, {
        detail: {disableCohortize: !on},
      }),
    );

    this.track(Events.Cohortize, {
      report: `retention`,
      Cohortize: on,
    });
  };
}

export const RetentionCriteriaEl = defineAndMakeJsxFactory<RetentionCriteriaJsxProps>(
  `retention-criteria`,
  RetentionCriteria,
);
export const RetentionCriteriaReact = panel2react<RetentionCriteriaJsxProps>(`retention-criteria`, RetentionCriteria);
